import AutoFixHighRounded from '@mui/icons-material/AutoFixHighRounded';
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ComponentProps } from 'react';
import {
  Controller,
  get,
  useFormState,
  useWatch,
  type Control,
  type FieldValues,
  type Path,
  type UseFormSetValue,
} from 'react-hook-form';
import { useShop } from '../../shop/ShopProvider';
import { marginPercent, type Attribute, type VariantForm } from './model';

type WithVariants = FieldValues & { variants: VariantForm[] };

/**
 * The fields of one variant inside any form that holds `variants[]` (the new-product page edits
 * many, the variant dialog edits one at index 0).
 */
export function VariantFields<T extends WithVariants>({
  control,
  setValue,
  index,
  attributes,
  baseSymbol,
  trackInventory,
  canPrice,
  canViewCost,
  onSuggestSku,
}: {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  index: number;
  attributes: Attribute[];
  baseSymbol: string;
  trackInventory: boolean;
  canPrice: boolean;
  canViewCost: boolean;
  onSuggestSku?: () => string;
}) {
  const { format } = useShop();
  const name = (field: string) => `variants.${String(index)}.${field}` as Path<T>;
  const { errors } = useFormState({ control });
  const err = (field: string): string | undefined =>
    (get(errors, name(field)) as { message?: string } | undefined)?.message;
  const [purchase, sale, values] = useWatch({
    control,
    name: [name('purchasePrice'), name('salePrice'), name('attributes')],
  }) as [string, string, Record<string, string> | undefined];
  const margin = marginPercent(purchase.trim() || null, sale.trim());
  const shownAttributes = attributes.filter((a) => a.isActive || (values?.[a.id] ?? '') !== '');

  const text = (
    field: keyof VariantForm,
    label: string,
    extra: Partial<ComponentProps<typeof TextField>> = {},
  ) => (
    <Controller
      control={control}
      name={name(field)}
      render={({ field: f }) => (
        <TextField
          label={label}
          value={f.value}
          onChange={f.onChange}
          onBlur={f.onBlur}
          inputRef={f.ref}
          error={!!err(field)}
          {...extra}
          // Labels always sit above: several fields carry adornments, and a row reads evenly.
          slotProps={{ ...extra.slotProps, inputLabel: { shrink: true } }}
          helperText={err(field) ?? extra.helperText ?? ' '}
        />
      )}
    />
  );
  const money = (adornment: string) => ({
    input: { startAdornment: <InputAdornment position="start">{adornment}</InputAdornment> },
    htmlInput: { inputMode: 'decimal' as const, className: 'tabular' },
  });

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
      }}
    >
      <Box sx={{ gridColumn: { sm: 'span 2' } }}>
        {text('nameSuffix', 'Variant name', {
          placeholder: 'e.g. 24x24 Glossy',
          helperText: 'Added after the product name on documents',
          fullWidth: true,
        })}
      </Box>
      {text('sku', 'SKU', {
        required: true,
        slotProps: onSuggestSku
          ? {
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Suggest from the product code">
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label="Suggest SKU"
                        onClick={() => {
                          const sku = onSuggestSku();
                          if (sku)
                            setValue(name('sku'), sku as never, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                        }}
                      >
                        <AutoFixHighRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }
          : undefined,
      })}
      {text('barcode', 'Barcode', { placeholder: 'Scan or type', helperText: 'Optional' })}

      {text('salePrice', `Sale price per ${baseSymbol}`, {
        required: true,
        disabled: !canPrice,
        helperText: canPrice ? undefined : 'You can’t change prices',
        slotProps: money(format.currencyCode),
      })}
      {canViewCost &&
        text('purchasePrice', `Cost per ${baseSymbol}`, {
          disabled: !canPrice,
          helperText:
            margin === null
              ? 'Optional · last purchase cost'
              : `Margin ${margin.toFixed(1)}%${margin < 0 ? ' — below cost' : ''}`,
          slotProps: money(format.currencyCode),
        })}
      {trackInventory && (
        <>
          {text('minStockLevel', 'Low-stock alert at', {
            helperText: 'Optional',
            slotProps: {
              input: { endAdornment: <InputAdornment position="end">{baseSymbol}</InputAdornment> },
              htmlInput: { inputMode: 'decimal' },
            },
          })}
          {text('reorderQty', 'Reorder quantity', {
            helperText: 'Optional',
            slotProps: {
              input: { endAdornment: <InputAdornment position="end">{baseSymbol}</InputAdornment> },
              htmlInput: { inputMode: 'decimal' },
            },
          })}
        </>
      )}

      {shownAttributes.length > 0 && (
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Attributes
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'minmax(0,1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
            }}
          >
            {shownAttributes.map((a) => (
              <Controller
                key={a.id}
                control={control}
                name={name(`attributes.${a.id}`)}
                render={({ field: f, fieldState }) => {
                  const unit = a.unitHint ? (
                    <InputAdornment position="end">{a.unitHint}</InputAdornment>
                  ) : undefined;
                  return a.dataType === 'OPTION' ? (
                    <TextField
                      select
                      label={a.name}
                      value={f.value ?? ''}
                      onChange={f.onChange}
                      onBlur={f.onBlur}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
                    >
                      <MenuItem value="">
                        <Typography color="text.secondary">Not set</Typography>
                      </MenuItem>
                      {a.options
                        .filter((o) => o.isActive || o.id === f.value)
                        .map((o) => (
                          <MenuItem key={o.id} value={o.id}>
                            {o.value}
                            {a.unitHint ? ` ${a.unitHint}` : ''}
                          </MenuItem>
                        ))}
                    </TextField>
                  ) : (
                    <TextField
                      label={a.name}
                      value={f.value ?? ''}
                      onChange={f.onChange}
                      onBlur={f.onBlur}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      placeholder={a.dataType === 'NUMBER' ? 'e.g. 9' : undefined}
                      slotProps={{
                        inputLabel: { shrink: true },
                        input: { endAdornment: unit },
                        htmlInput: a.dataType === 'NUMBER' ? { inputMode: 'decimal' } : undefined,
                      }}
                    />
                  );
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
